<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link } from 'svelte-navigator';
  import { ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  export let route = {};
  export let dropIndex = null;
  export let routeIndex = null;
  export let startIndex = null;
  export let reordering = false;
  export let priority = [];
  export let updatePriorityPositions = () => {};
  export let onUpdate = () => {};
  export let onEdit = () => {};

  export let onDelete = () => {};
  export let editing = false;

  /**
   * Whether the page is in selection mode. Renders a checkbox, suppresses the
   * kebab menu, and turns card clicks into toggles.
   * @type {boolean}
   */
  export let selecting = false;

  /**
   * Whether this target is currently selected.
   * @type {boolean}
   */
  export let selected = false;

  /**
   * Called with this target when the card or its checkbox is clicked while selecting.
   * @type {(route: Object) => void}
   */
  export let onSelect = () => {};

  const { basepath } = getContext('config');

  const drop = (e) => {
    e.dataTransfer.dropEffect = 'move';
    priority = updatePriorityPositions(priority, startIndex, dropIndex);
    dropIndex = null;
    startIndex = null;
  };

  const dragstart = (e) => {
    if (!reordering) {
      e.preventDefault();
      return false;
    }

    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.dropEffect = 'move';

    startIndex = routeIndex;
  };

  const onDeleteHandler = async () => {
    onDelete(route);
  };

  const onEditHandler = () => {
    onEdit(route);
  };

  const onDisableHandler = () => {
    route.status = false;
    onUpdate();
  };

  const onEnableHandler = () => {
    route.status = true;
    onUpdate();
  };

  // Group links keep working while selecting, so clicks on them are not toggles.
  const onCardClick = ({ target }) => {
    if (!selecting || target.closest('a, button, input')) {
      return;
    }

    onSelect(route);
  };
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  class="route"
  class:reordering
  class:editing
  class:disabled={!reordering}
  class:active={reordering && dropIndex === routeIndex}
  class:selecting
  class:border-primary={selected}
  class:bg-primary-subtle={selected}
  draggable={reordering}
  role="button"
  tabindex="0"
  on:dragstart={dragstart}
  on:drop|preventDefault={drop}
  on:dragover|preventDefault
  on:dragenter|preventDefault={() => (dropIndex = routeIndex)}
  on:click={onCardClick}
>
  {#if reordering}
    <Icon name="grip-vertical" class="position-absolute text-body-tertiary text-large cursor-move" style="left: 8px" />
  {/if}

  {#if selecting}
    <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
    <div class="select position-absolute" style="left: 8px" on:click|stopPropagation>
      <input
        class="form-check-input"
        type="checkbox"
        checked={selected}
        on:change={() => onSelect(route)}
        aria-label="{$format('label.SELECT')} {$format('label.GROUP_TARGET')} {route.priority}"
      />
    </div>
  {/if}

  <div class="priority-badge" class:active={route.status}>{route.priority}</div>

  <div class="route-section">
    <b>{$format('label.TARGET')}</b>
    {route.targettype === 'sender' ? $format('label.SENDER') : $format('label.RECIPIENT')}
  </div>
  <div class="route-section">
    <b>{$format('label.GROUPS')}</b>
    {#if route.groups?.length}
      <div class="items">
        {#each route.groups as group, index}
          <div class="item">
            <a href="{basepath}/manage/groups/{group.label}" use:link>{group.label}</a>
          </div>
        {/each}
      </div>
    {:else}
      <div class="muted">{$format('label.NO_ASSIGNED_GROUPS')}</div>
    {/if}
  </div>
  <div class="route-section">
    <b>{$format('label.CAMPAIGNS')}</b>
    {#if route.campaigns?.length}
      <div class="items">
        {#each route.campaigns as campaign}
          <div class="item">{campaign.label}</div>
        {/each}
      </div>
    {:else}
      <div class="muted">{$format('label.NO_ASSIGNED_CAMPAIGNS')}</div>
    {/if}
  </div>
  <div class="route-section">
    <b>{$format('label.TAGS')}</b>
    {#if route.tags?.length}
      <div class="items">
        {#each route.tags as tag}
          <div class="item">{tag.label}</div>
        {/each}
      </div>
    {:else}
      <div class="muted">{$format('label.NO_ASSIGNED_TAGS')}</div>
    {/if}
  </div>
  <div class="actions">
    {#if !reordering && !selecting}
      <ButtonDropdown>
        <DropdownToggle color="none" tag="span">
          <Icon name="more-vertical" class="action-icon" />
        </DropdownToggle>
        <DropdownMenu>
          <DropdownItem on:click={onEditHandler}>{$format('label.EDIT')}</DropdownItem>
          {#if route.status}
            <DropdownItem on:click={onDisableHandler}>{$format('label.DISABLE')}</DropdownItem>
          {:else}
            <DropdownItem on:click={onEnableHandler}>{$format('label.ENABLE')}</DropdownItem>
          {/if}
          <DropdownItem divider />
          <DropdownItem on:click={onDeleteHandler}>{$format('label.DELETE')}</DropdownItem>
        </DropdownMenu>
      </ButtonDropdown>
    {/if}
  </div>
</div>

<style lang="scss" src="./route.scss"></style>
